'use client'

import * as SelectPrimitive from '@radix-ui/react-select'
import clsx from 'clsx'
import { type ComponentProps, createContext, type ReactNode, useContext, useEffect, useState } from 'react'
import { ChevronDown, ChevronUp } from 'react-bootstrap-icons'
import { CheckIcon } from '../../icons/CheckIcon'
import { ChevronIcon } from '../../icons/Chevron'
import { WarningTriangleIcon } from '../../icons/WarningTriangleIcon'
import { xStack, yStack } from '../../patterns/stack'
import { tokens } from '../../theme/tokens.css'
import {
  content,
  item,
  label,
  scrollButton,
  triggerIcon,
  fullWidth as fullWidthStyles,
  triggerSize,
} from './Select.css'
import { trigger } from './Select.css'

type Variant = 'neutral' | 'danger' | 'warning'

type SelectContentValue = {
  value?: string | null
  variant: Variant
}

const SelectContext = createContext<SelectContentValue | null>(null)

const useSelectContext = () => {
  const context = useContext(SelectContext)

  if (!context) {
    throw new Error('useSelectContext must be used within a Select')
  }

  return context
}

type Props = ComponentProps<typeof SelectPrimitive.Root> & {
  variant?: Variant
}

function SelectRoot({ children, defaultValue, value, onValueChange, variant = 'neutral', ...rest }: Props) {
  // Internal value is neded to handle uncontrolled usage
  const [internalValue, setInternalValue] = useState(value ?? defaultValue)

  // It needs to be synced with provided value in controlled usage
  useEffect(() => {
    // Only sync if value is provided
    if (value != null) {
      setInternalValue(value)
    }
  }, [value])

  return (
    <SelectContext.Provider value={{ value: internalValue, variant }}>
      <SelectPrimitive.Root
        value={value}
        defaultValue={defaultValue}
        onValueChange={(value) => {
          setInternalValue(value)
          onValueChange?.(value)
        }}
        {...rest}
      >
        {children}
      </SelectPrimitive.Root>
    </SelectContext.Provider>
  )
}

type TriggerProps = ComponentProps<typeof SelectPrimitive.Trigger> & {
  fullWidth?: boolean
  size?: 'small' | 'medium' | 'large'
}

function Trigger({ children, className, fullWidth, size = 'medium', ...props }: TriggerProps) {
  const { value, variant } = useSelectContext()
  const hasValue = value != null && value !== ''

  return (
    <SelectPrimitive.Trigger
      data-selected={hasValue}
      className={clsx(trigger[variant], triggerSize[size], className, {
        [fullWidthStyles]: !!fullWidth,
      })}
      {...props}
    >
      {children}

      <div className={xStack({ gap: 'xxxs', alignItems: 'center' })}>
        {(variant === 'warning' || variant === 'danger') && (
          <WarningTriangleIcon fill={'currentColor'} className={triggerIcon} />
        )}

        <SelectPrimitive.Icon>
          <ChevronIcon size="1rem" color={tokens.colors.fills.opaque.primary} />
        </SelectPrimitive.Icon>
      </div>
    </SelectPrimitive.Trigger>
  )
}

function Label({ children, className, ...props }: ComponentProps<'span'>) {
  const { value } = useSelectContext()
  const hasValue = value != null && value !== ''

  return (
    <>
      {/* Hidden element to maintain space */}
      {!hasValue && (
        <span className={clsx(label, className)} style={{ visibility: 'hidden' }} aria-hidden="true" {...props}>
          {children}
        </span>
      )}

      {/* Visible label that animates */}
      <span className={clsx(label, className)} {...props}>
        {children}
      </span>
    </>
  )
}

function SelectContent({ children, className, ...rest }: ComponentProps<typeof SelectPrimitive.Content>) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content position="popper" side="bottom" className={clsx(content, className)} {...rest}>
        {children}
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  )
}

function SelectOptions({ children }: { children: ReactNode }) {
  return (
    <>
      <SelectPrimitive.ScrollUpButton className={scrollButton}>
        <ChevronUp />
      </SelectPrimitive.ScrollUpButton>

      <SelectPrimitive.Viewport className={yStack({ gap: 'xs' })}>{children}</SelectPrimitive.Viewport>

      <SelectPrimitive.ScrollDownButton className={scrollButton}>
        <ChevronDown />
      </SelectPrimitive.ScrollDownButton>
    </>
  )
}

function SelectItem({ children, className, ...rest }: ComponentProps<typeof SelectPrimitive.Item>) {
  return (
    <SelectPrimitive.Item className={clsx(item, className)} {...rest}>
      {children}
    </SelectPrimitive.Item>
  )
}

function SelectItemText({ children, className, ...rest }: ComponentProps<typeof SelectPrimitive.ItemText>) {
  return (
    <SelectPrimitive.ItemText className={className} {...rest}>
      {children}
    </SelectPrimitive.ItemText>
  )
}

type SelectItemIndicatorProps = Omit<ComponentProps<typeof SelectPrimitive.ItemIndicator>, 'children'>
function SelectItemIndicator(props: SelectItemIndicatorProps) {
  return (
    <SelectPrimitive.ItemIndicator {...props}>
      <CheckIcon size="16px" />
    </SelectPrimitive.ItemIndicator>
  )
}

function Context({ children }: { children: (context: SelectContentValue) => ReactNode }) {
  const context = useSelectContext()
  return <>{children(context)}</>
}

export const Select = {
  Root: SelectRoot,
  Trigger: Trigger,
  Label: Label,
  Value: SelectPrimitive.Value,
  Content: SelectContent,
  Options: SelectOptions,
  Item: SelectItem,
  ItemText: SelectItemText,
  ItemIndicator: SelectItemIndicator,
  Context: Context,
}
